<template>
	<div class="form-group">
		<div v-if="showIdentityCard" class="mb-3">
			<div class="mb-3">
				<modal
					v-if="openIdCardModal"
					size="medium"
					:hide-footer="true"
					@close="closeModal">
					<div slot="header">
						<h3>
							<b>Identity Card</b>
						</h3>
					</div>
					<div slot="body" class="d-flex justify-content-center">
						<img :src="idCardUrl" />
					</div>
				</modal>

				<button
					v-else
					class="btn btn-primary w-100"
					type="button"
					:class="{ 'is-loading': isFetching }"
					@click="fetchIdCard">
					{{ $t("Show Identity Card") }}
				</button>
			</div>
		</div>

		<label><b>{{ $t('Features') }}</b></label>
		<div class="input-group">
			<select
				type="text"
				name="feature"
				class="form-control"
				:placeholder="$t('Add Feature')"
				:disabled="featuresOptions.length === 0"
				v-model="selectedFeature">
				<option
					v-for="feature in featuresOptions"
					:key="feature.value"
					:value="feature.value">
					{{ feature.label }}
				</option>
			</select>
			<button
				class="btn btn-primary"
				type="button"
				:class="{ 'is-loading': isAdding }"
				:disabled="!selectedFeature"
				@click="add">
				{{ $t('Add Feature') }}
			</button>
		</div>
		<div class="selected-options mt-3">
			<div
				class="selected-option bg-grey"
				v-for="feature in features"
				:key="feature">
				<span>{{ getLabel(feature) }}</span>
				<i
					class="material-icons selected-option-remove"
					:class="{ 'is-loading': isRemoving }"
					v-tooltip="'Remove Feature'"
					@click="remove(feature)">close</i>
			</div>
		</div>
	</div>
</template>

<script>
import { userFeatures, featureOption } from '@/lib/statics';
import adminApi from '@/api/admin';
import utilitiesApi from '@/api/utilities';
import { getAxiosErrorMessage } from '@/lib/helper';
import Modal from '@/components/Modal.vue';

export default {
	name: 'UserFeatures',
	components: {
		Modal,
	},
	props: {
		user: {
			type: Object,
		},
		showIdentityCard: {
			type: Boolean,
			default: () => true,
		},
		type: {
			type: String,
			default: 'all',
		},
	},
	data() {
		return {
			selectedFeature: null,
			features: [],
			idCardUrl: '',
			isAdding: false,
			isRemoving: false,
			isFetching: false,
			openIdCardModal: false,
		};
	},
	methods: {
		add() {
			const feature = this.selectedFeature;
			const userName = this.user.name;
			if (feature) {
				this.isAdding = true;
				const featureLabel = this.getLabel(feature);
				const userIds = [this.user._id];
				const params = {
					user_ids: JSON.stringify(userIds),
					feature,
				};
				const callback = () => {
					const message = `Feature ${featureLabel} has been added to ${userName}`;
					this.$notify({
						group: 'app',
						type: 'success',
						title: 'Add Feature',
						text: message,
					});
					this.features.push(feature);
					this.isAdding = false;
					this.$emit('updateUser', feature);
				};
				const errorCallback = (e) => {
					const message = getAxiosErrorMessage(e);
					this.$notify({
						group: 'app',
						type: 'danger',
						title: 'Add Feature',
						text: message,
					});
					this.isAdding = false;
				};
				adminApi.addFeature(params, callback, errorCallback);
			}
			this.selectedFeature = null;
		},
		remove(feature) {
			this.isRemoving = true;
			const userName = this.user.name;
			const featureLabel = this.getLabel(feature);
			const userIds = [this.user._id];
			const params = {
				user_ids: JSON.stringify(userIds),
				feature,
			};
			const callback = () => {
				const message = `Feature ${featureLabel} has been removed from ${userName}`;
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Remove Feature',
					text: message,
				});
				const index = this.features.findIndex(value => value === feature);
				if (index !== -1) {
					this.features.splice(index, 1);
				}
				this.isRemoving = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'danger',
					title: 'Remove Feature',
					text: message,
				});
				this.isRemoving = false;
			};
			adminApi.removeFeature(params, callback, errorCallback);
		},
		getLabel(feature) {
			const userFeature = userFeatures.find(({ value }) => value === feature);
			const label = userFeature ? userFeature.label : feature;
			return label;
		},
		fetchIdCard() {
			this.isFetching = true;

			const params = { user_id: this.user._id };
			const callback = (response) => {
				this.isFetching = false;
				this.idCardUrl = response.data;
				this.openIdCardModal = true;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				console.log(message);
				this.$notify({
					group: 'app',
					type: 'danger',
					title: 'Fetch ID Card',
					text: message,
				});
				this.isFetching = false;
			};

			if (this.idCardUrl === '') {
				utilitiesApi.getPresignedUrl(params, callback, errorCallback);
			} else {
				this.isFetching = false;
				this.openIdCardModal = true;
			}
		},
		closeModal() {
			this.openIdCardModal = false;
		},
	},
	computed: {
		userId() {
			return this.user._id;
		},
		featuresOptions() {
			const filter = feature => !this.features.includes(feature.value);
			let options = userFeatures.filter(filter);
			if (this.type !== 'all') {
				options = featureOption[this.type].filter(filter);
			}
			return options;
		},
	},
	created() {
		this.features = this?.user?.features ?? [];
	},
};
</script>
